import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import BackgroundImage from '../BackgroundImage';
import VisibilitySensor from 'react-visibility-sensor';
import { useTranslation } from 'next-i18next';

import { serializeImage } from '../../utils/SerializeImage';
import debounce from '../../utils/debounce';
import { breakpoints } from '../../constants';

import classNames from 'classnames';
import isEmpty from '../../utils/isEmpty';
import useMediaQuery from '../../utils/useMediaQuery';
import AnchorLink from '../AnchorLink';

import styles from './ArticleList.module.scss';

const useScrollPosition = (elem) => {
    const [scrollPosition, setScrollPosition] = useState(0);

    const listener = debounce(() => {
        setScrollPosition(elem.current.scrollLeft);
    }, 200);

    useEffect(() => {
        if (!elem.current) {
            return;
        }

        const el = elem.current;

        el.addEventListener('scroll', listener, { passive: true });
        return () => {
            el.removeEventListener('scroll', listener);
        };
    }, [elem, listener]);

    return {
        scrollPosition,
    };
};

const ArticleList = ({
    id,
    items = [],
    hTag = 'h2',
    href = '',
    title = '',
    preamble = '',
    usp = '',
    landingType = '',
    isPremium = false,
    whereToGoType = '',
    isRootWhereToGo = false,
    anchorLink = '',
    largeImage = false,
    modifier = '',
    isRekAI = false,
    // isLandingPage,
}) => {
    const { t } = useTranslation('common');
    const sliderElement = useRef(null);
    const { scrollPosition } = useScrollPosition(sliderElement);
    const isTablet = useMediaQuery({
        query: `(max-width: ${breakpoints.l}px)`,
    });

    const Heading = hTag;
    const cardHeadingTag = !!title ? (hTag === 'h2' ? 'h3' : 'h4') : hTag;

    const keys = [32, 13];

    const slide = (dir, e) => {
        e.preventDefault();

        if (e.keyCode && keys.indexOf(e.keyCode) === -1) {
            return;
        }

        if (!sliderElement.current) {
            return;
        }

        const firstCard = sliderElement.current.querySelector('a');
        if (!firstCard) return;

        const cardStyle = window.getComputedStyle(firstCard);
        const cardWidth = firstCard.offsetWidth + parseFloat(cardStyle.marginRight);

        const cardsToScroll = isTablet ? 1 : 2;
        const scrollLength = cardWidth * cardsToScroll;

        let scroll = {
            behavior: 'smooth',
        };

        if (dir === 'left') {
            scroll.left = scrollPosition - scrollLength;
        } else {
            scroll.left = scrollPosition + scrollLength;
        }

        sliderElement.current.scroll(scroll);
    };

    if (isEmpty(items)) {
        return null;
    }

    // const isSingle = items.length === 1;

    const slideRight = (e) => slide('right', e);
    const slideLeft = (e) => slide('left', e);

    const percentageScrolled = sliderElement.current
        ? (scrollPosition / (sliderElement.current.scrollWidth - sliderElement.current.clientWidth)) * 100
        : 0;
    const isAtStart = scrollPosition === 0;
    const isAtEnd = percentageScrolled >= 99; // Using 99 instead of 100 to account for rounding

    const getVisibleItemsCount = () => {
        if (isTablet) {
            return 2;
        } else {
            return 4;
        }
    };

    const visibleItemsCount = getVisibleItemsCount();
    const isScrollable = items.length > visibleItemsCount;

    const classes = classNames(styles['ArticleList'], {
        [styles['ArticleList--NonPremium']]: !isPremium,
        [styles['ArticleList--Premium']]: isPremium,
        // [styles["ArticleList--Single"]]: isSingle,
        [styles['ArticleList--Northern']]: isRootWhereToGo && whereToGoType == 'northern-sweden',
        [styles['ArticleList--Southern']]: isRootWhereToGo && whereToGoType == 'southern-sweden',
        [styles['ArticleList--Central']]: isRootWhereToGo && whereToGoType == 'middle-sweden',
        // [styles["ArticleList--Narrow"]]: items.length <= 3 && !isLandingPage,
        [styles['ArticleList--HasIndicator']]: items.length > 4,
        [styles['ArticleList--LargeImage']]: largeImage,
        [styles[`ArticleList--${modifier}`]]: modifier,
    });

    const titleClasses = classNames(styles['ArticleList__Title'], {
        [styles['ArticleList__Title--Linked']]: href,
    });

    return (
        <div className={classes}>
            <div className={styles['ArticleList__Container']}>
                <div className={styles['ArticleList__TextContainer']}>
                    {href && (
                        <Heading className={titleClasses}>
                            <a href={href}>
                                <span className="sr-only">{title}</span>
                            </a>
                            <span>{title}</span>
                            <span className={styles['ArticleList__ShowMore']} aria-hidden={true}>
                                <span className={styles['ArticleList__ShowMoreText']}></span>
                            </span>
                        </Heading>
                    )}
                    {!href && (
                        <Heading className={styles['ArticleList__Title']}>
                            <AnchorLink title={title} id={anchorLink} />
                        </Heading>
                    )}
                    {(preamble || usp) && <div className={styles['ArticleList__Preamble']}>{usp || preamble}</div>}
                    {/* Desktop arrows */}
                    <div className={styles['ArticleList__ArrowsDesktop']}>
                        {isScrollable && (
                            <button
                                className={styles['ArticleList__ArrowLeft']}
                                type="button"
                                onClick={(e) => slideLeft(e)}
                                disabled={isAtStart}>
                                <span className="sr-only">{t('articleList.scrollLeft')}</span>
                            </button>
                        )}
                        {isScrollable && (
                            <button
                                className={styles['ArticleList__ArrowRight']}
                                type="button"
                                onClick={(e) => slideRight(e)}
                                disabled={isAtEnd}>
                                <span className="sr-only">{t('articleList.scrollRight')}</span>
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles['ArticleList__SlideWrap']}>
                    <div className={styles['ArticleList__Slidable']} tabIndex={-1} ref={sliderElement}>
                        {items.map((item, i) => (
                            <ArticleListCard
                                {...item}
                                landingType={landingType}
                                hTag={cardHeadingTag}
                                landingId={id}
                                index={i}
                                key={i}
                                isRekAI={isRekAI}
                                largeImage={largeImage}
                            />
                        ))}
                    </div>
                    {/* Mobile arrows */}
                    <div className={styles['ArticleList__ArrowsMobile']}>
                        {isScrollable && (
                            <button
                                className={styles['ArticleList__ArrowLeft']}
                                type="button"
                                onClick={(e) => slideLeft(e)}
                                disabled={isAtStart}>
                                <span className="sr-only">{t('articleList.scrollLeft')}</span>
                            </button>
                        )}
                        {isScrollable && (
                            <button
                                className={styles['ArticleList__ArrowRight']}
                                type="button"
                                onClick={(e) => slideRight(e)}
                                disabled={isAtEnd}>
                                <span className="sr-only">{t('articleList.scrollRight')}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

ArticleList.propTypes = {
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    items: PropTypes.array,
    title: PropTypes.string,
    hTag: PropTypes.string,
    preamble: PropTypes.string,
    usp: PropTypes.string,
    href: PropTypes.string,
    anchorLink: PropTypes.string,
    isPremium: PropTypes.bool,
    isLandingPage: PropTypes.bool,
    isLeaf: PropTypes.bool,
    isRootWhereToGo: PropTypes.bool,
    whereToGoType: PropTypes.string,
    landingType: PropTypes.string,
    largeImage: PropTypes.bool,
    isRekAI: PropTypes.bool,
    modifier: PropTypes.string,
};

const ArticleListCard = ({
    image = {},
    title = '',
    hTag = 'h3',
    tag = '',
    href = '',
    url = '',
    type = '',
    landingType = '',
    landingId,
    index,
    isRekAI = false,
    largeImage = false,
}) => {
    const { t } = useTranslation('common');
    const [isVisible, setIsVisible] = useState(index === 0);

    image = isRekAI ? image : serializeImage(image);

    const hasImage = image && (image.file || image.src);

    // LargeImage is 296x420 (aspect ratio 1:1.42), needs larger size for object-fit:cover
    // Balance between quality and file size: higher sizes for sharpness, moderate quality for compression
    const sizes = largeImage ? '600px' : '450px';

    const classes = classNames(styles['ArticleList__Card'], styles[`ArticleList__Card--${type}`], {
        [styles['ArticleList__Card--NoImage']]: !hasImage,
    });

    const tagMap = {
        external: t('articleList.tagExternal'),
        city: t('articleList.tagCity'),
        region: t('articleList.tagRegion'),
    };

    const tagTitle = tag?.name || (type && tagMap[type]) || '';

    const itemHref = href || url;

    if (landingType === 'what-to-do' && landingId) {
        href = `${href}?what_to_do=${landingId}`;
    }

    const Heading = hTag;

    return (
        <VisibilitySensor onChange={(x) => setIsVisible(index === 0 || x)}>
            <article className={classes}>
                <a href={itemHref} className={styles['ArticleList__CardLink']} tabIndex={isVisible ? 0 : -1}>
                    {hasImage && (
                        <div className={styles['ArticleList__Image']}>
                            {<BackgroundImage {...image} quality={75} sizes={sizes} hideAlt={true} />}
                            {tagTitle && (
                                <div aria-hidden={true} className={styles['ArticleList__Tag']}>
                                    {tagTitle}
                                </div>
                            )}
                        </div>
                    )}
                    <div className={styles['ArticleList__CardContent']}>
                        <Heading className={styles['ArticleList__CardTitle']}>{title}</Heading>
                    </div>
                </a>
            </article>
        </VisibilitySensor>
    );
};

ArticleListCard.propTypes = {
    image: PropTypes.object,
    title: PropTypes.string,
    href: PropTypes.string,
    type: PropTypes.string,
    landingType: PropTypes.string,
    landingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    index: PropTypes.number,
    isRekAI: PropTypes.bool,
    largeImage: PropTypes.bool,
};

const GeneralArticleList = ({ items, landingType }) => {
    const { t } = useTranslation('common');
    const title =
        landingType === 'where-to-go' ? t('articleList.generalWhereToGoTitle') : t('articleList.generalWhatToDoTitle');

    return <ArticleList isLandingPage={true} items={items} title={title} />;
};

const MoreInArticleList = ({ items, parentTitle }) => {
    const { t } = useTranslation('common');
    return <ArticleList isLandingPage={true} items={items} title={t('articleList.moreIn', { parentTitle })} />;
};

GeneralArticleList.propTypes = {
    items: PropTypes.array,
    landingType: PropTypes.string,
};
MoreInArticleList.propTypes = {
    items: PropTypes.array,
    landingType: PropTypes.string,
    parentTitle: PropTypes.string,
};

export { GeneralArticleList, MoreInArticleList };
export default ArticleList;
